import { useState, useEffect, useCallback, Suspense } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAppState } from "../../context/appStateContext";
import { useLicense } from "../../context/licenseContext";
import useSettingsPermissions from "./hooks/useSettingsPermissions";
import SettingsSidebar from "./SettingsSidebar";
import Loading from "../../components/Loading/Loading";
import apiClient from "../../api/apiClient";
import "./SettingsLayout.css";

const TITLES = {
    "/settings/company/profile": "Company Profile",
    "/settings/company/employees": "Employees",
    "/settings/company/locations": "Locations",
    "/settings/company/payments": "Payments",
    "/settings/company/pricing": "Pricing",
    "/settings/account/profile": "Your Account",
    "/settings/account/preferences": "Preferences",
    "/settings/help/faq": "FAQ",
    "/settings/help/tutorials": "Tutorials",
};

export default function SettingsLayout({ setHeaderAndMenu, setHeaderTitle }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { setUser } = useAppState();
    const { setCurrentLicense } = useLicense();
    const perms = useSettingsPermissions();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

    useEffect(() => {
        const mq = window.matchMedia("(max-width: 768px)");
        const handler = (e) => setIsMobile(e.matches);
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
    }, []);

    useEffect(() => {
        const title = TITLES[location.pathname] || "Settings";
        setHeaderTitle(`Settings / ${title}`);
    }, [location.pathname, setHeaderTitle]);

    useEffect(() => {
        setDrawerOpen(false);
    }, [location.pathname]);

    const handleLogout = useCallback(async () => {
        let tokens = null;
        try {
            const tokensString = localStorage.getItem("tokens");
            if (tokensString) tokens = JSON.parse(tokensString);
        } catch {
            localStorage.removeItem("tokens");
        }
        if (tokens?.AccessToken) {
            try {
                const response = await apiClient.post("/user/logout", {
                    accessToken: tokens.AccessToken,
                });
                if (response.status === 200) {
                    localStorage.removeItem("tokens");
                    localStorage.removeItem("currentLicenseOption");
                    localStorage.removeItem("currentLicense");
                    localStorage.removeItem("currentPermissions");
                    localStorage.removeItem("tutorial_status");
                    localStorage.removeItem("tutorial_last_step");
                    localStorage.removeItem("tutorial_phase");
                    setCurrentLicense(null);
                    setUser(null);
                    setHeaderAndMenu(false);
                    navigate("/");
                }
            } catch (error) {
                console.error("Logout error:", error);
            }
        }
    }, [navigate, setCurrentLicense, setUser, setHeaderAndMenu]);

    return (
        <div className="settings-layout">
            {isMobile && (
                <button
                    className="settings-layout__drawer-toggle"
                    onClick={() => setDrawerOpen(true)}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="3" y1="6" x2="21" y2="6" />
                        <line x1="3" y1="12" x2="21" y2="12" />
                        <line x1="3" y1="18" x2="21" y2="18" />
                    </svg>
                    Settings Menu
                </button>
            )}

            <SettingsSidebar
                perms={perms}
                isMobile={isMobile}
                isOpen={!isMobile || drawerOpen}
                onClose={() => setDrawerOpen(false)}
                onLogout={handleLogout}
            />

            <div className="settings-layout__content">
                <Suspense fallback={<Loading />}>
                    <Outlet context={{ perms, handleLogout }} />
                </Suspense>
            </div>
        </div>
    );
}
